import { useEffect, useState } from "react";
import { getReservations, fmt, fmtDate, downloadCsv } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import ReservationDrawer from "@/components/ReservationDrawer";
import AddReservationDialog from "@/components/AddReservationDialog";

const STATUS_STYLES = {
  booked: "bg-slate-100 text-slate-700 border-slate-200",
  checked_in: "bg-blue-100 text-blue-700 border-blue-200",
  checked_out: "bg-slate-200 text-slate-600 border-slate-300",
};
const PAY_STYLES = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  partial: "bg-amber-100 text-amber-700 border-amber-200",
  unpaid: "bg-red-100 text-red-700 border-red-200",
};

export default function Reservations() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    const data = await getReservations(params);
    setRows(data.items || []);
    setTotal(data.total || 0);
    setPages(data.pages || 0);
  };

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [search, statusFilter, page]);

  const openDrawer = (id) => { setSelectedId(id); setDrawerOpen(true); };

  const handleCsv = () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    downloadCsv("/reservations.csv", params, `reservations_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Reservations</h1>
          <p className="text-sm text-slate-500 mt-1">{total} reservation{total === 1 ? "" : "s"} · click any row to open details</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCsv} className="h-11" data-testid="reservations-csv-btn">
            <Download size={14} /> CSV
          </Button>
          <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="reservations-new-btn">
            + New Reservation
          </Button>
        </div>
      </div>

      <Card className="p-4 shadow-none border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <Input
              placeholder="Search by guest name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
              data-testid="reservations-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-10" data-testid="reservations-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="checked_in">Checked-in</SelectItem>
              <SelectItem value="checked_out">Checked-out</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="shadow-none border-slate-200 overflow-hidden" data-testid="reservations-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Guest</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const s = r.summary || {};
              const remRed = (s.remaining || 0) > 0.01;
              return (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-blue-50/40 border-slate-100"
                  onClick={() => openDrawer(r.id)}
                  data-testid={`reservation-row-${r.id}`}
                >
                  <TableCell>
                    <div className="font-medium text-slate-900">{r.guest_name}</div>
                    {r.guest_email && <div className="text-xs text-slate-500">{r.guest_email}</div>}
                  </TableCell>
                  <TableCell className="text-slate-700 text-sm">{r.unit?.display_name || "—"}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{fmtDate(r.check_in)} → {fmtDate(r.check_out)}</TableCell>
                  <TableCell className="text-right font-medium">{fmt(s.total)}</TableCell>
                  <TableCell className="text-right text-emerald-700">{fmt(s.paid)}</TableCell>
                  <TableCell className={`text-right font-semibold ${remRed ? "text-red-600" : "text-slate-400"}`}>{fmt(s.remaining)}</TableCell>
                  <TableCell className="space-x-1.5">
                    <Badge variant="outline" className={STATUS_STYLES[r.status]}>{r.status.replace("_", " ")}</Badge>
                    {s.payment_status && <Badge variant="outline" className={PAY_STYLES[s.payment_status]}>{s.payment_status}</Badge>}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-400 text-sm">
                  No reservations. Click "+ New Reservation" to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between" data-testid="reservations-pagination">
          <div className="text-sm text-slate-500">
            Page {page} of {pages} · {total} total
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              data-testid="reservations-prev-page"
            >
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              data-testid="reservations-next-page"
            >
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      <ReservationDrawer
        reservationId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onChanged={load}
      />
      <AddReservationDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
    </div>
  );
}
